import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ isCollapsed = false, onToggle, isMobileOpen = false, onMobileClose }) => {
  const [userRole, setUserRole] = useState('employee');
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  // Define navigation items based on user role AND current route context
  const getNavigationItems = (role) => {
    // Check if we're on employee dashboard route
    const isOnEmployeeDashboard = location?.pathname === '/employee-dashboard';
    
    // If on employee dashboard, ONLY show employee navigation regardless of role
    // This prevents admin users from seeing admin nav when they shouldn't be on employee dashboard
    if (isOnEmployeeDashboard) {
      return [
        {
          label: 'Dashboard',
          path: '/employee-dashboard',
          icon: 'LayoutDashboard',
          tooltip: 'Personal overview and quick actions'
        },
        {
          label: 'Leave Management',
          path: '/leave-management',
          icon: 'Calendar',
          tooltip: 'Apply for leave and track requests'
        },
        {
          label: 'Attendance',
          path: '/attendance-tracking',
          icon: 'Clock',
          tooltip: 'View attendance records and reports'
        }
      ];
    }

    // Admin and HR Manager navigation items (only show on admin routes)
    if (role === 'admin' || role === 'hr_manager') {
      return [
        {
          label: 'Admin Dashboard',
          path: '/admin-dashboard',
          icon: 'LayoutDashboard',
          tooltip: 'Admin overview and management'
        },
        {
          label: 'Manage Employee',
          path: '/admin-dashboard?tab=employees',
          icon: 'Users',
          tooltip: 'Manage employees and records'
        },
        {
          label: 'Leave Management',
          path: '/admin-dashboard?tab=leave-management',
          icon: 'Calendar',
          tooltip: 'Approve and manage leave requests'
        },
        {
          label: 'Public Holidays',
          path: '/admin-dashboard?tab=public-holidays',
          icon: 'CalendarDays',
          tooltip: 'Manage public holidays and weekly offs'
        },
        {
          label: 'Payroll Management',
          path: '/admin-dashboard?tab=payroll',
          icon: 'DollarSign',
          tooltip: 'Process payroll and manage employee compensation'
        },
        {
          label: 'Attendance Tracking',
          path: '/attendance-tracking',
          icon: 'Clock',
          tooltip: 'View and manage attendance records'
        },
        {
          label: 'HR Reports',
          path: '/admin-dashboard?tab=hr-reports',
          icon: 'FileText',
          tooltip: 'Generate HR reports and analytics'
        },
        {
          label: 'Company Settings',
          path: '/company-settings',
          icon: 'Building',
          tooltip: 'Configure company details for salary slips and documents'
        },
        {
          label: 'System Settings',
          path: '/admin-dashboard?tab=system-overview',
          icon: 'Settings',
          tooltip: 'System configuration and settings'
        },
        {
          label: 'Profile',
          path: '/employee-profile',
          icon: 'User',
          tooltip: 'Manage personal information'
        }
      ];
    }

    // Employee navigation items (default)
    return [
      {
        label: 'Dashboard',
        path: '/employee-dashboard',
        icon: 'LayoutDashboard',
        tooltip: 'Personal overview and quick actions'
      },
      {
        label: 'Leave Management',
        path: '/leave-management',
        icon: 'Calendar',
        tooltip: 'Apply for leave and track requests'
      },
      {
        label: 'Attendance',
        path: '/attendance-tracking',
        icon: 'Clock',
        tooltip: 'View attendance records and reports'
      }
    ];
  };

  // Get navigation items based on current user role
  const navigationItems = getNavigationItems(userRole);

  // Update user role when userProfile changes
  useEffect(() => {
    if (userProfile?.role) {
      setUserRole(userProfile?.role);
    }
  }, [userProfile]);

  const handleNavigation = (path) => {
    navigate(path);
    if (onMobileClose) onMobileClose();
  };

  // Check if current path matches navigation item
  const isActiveItem = (item) => {
    if (item?.path?.includes('?tab=')) {
      const [basePath, queryString] = item?.path?.split('?');
      const urlParams = new URLSearchParams(location?.search);
      const itemParams = new URLSearchParams(queryString);
      const tabParam = itemParams?.get('tab');
      const currentTab = urlParams?.get('tab');
      
      return location?.pathname === basePath && currentTab === tabParam;
    }
    return location?.pathname === item?.path;
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && onMobileClose) {
        onMobileClose();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [onMobileClose]);

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo Section */}
      <div className="flex items-center px-4 py-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="Users" size={20} color="white" />
          </div>
          {!isCollapsed && (
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-foreground">HR Portal</span>
              <span className="text-xs text-muted-foreground">
                {userRole === 'admin' ? 'Admin Panel' : userRole === 'hr_manager' ? 'HR Manager' : 'Employee'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {navigationItems?.map((item, index) => {
          const isActive = isActiveItem(item);
          return (
            <button
              key={`${item?.path}-${index}`}
              onClick={() => handleNavigation(item?.path)}
              className={`
                w-full flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-smooth
                ${isActive 
                  ? 'bg-primary text-primary-foreground shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
                ${isCollapsed ? 'justify-center' : 'justify-start space-x-3'}
              `}
              title={isCollapsed ? item?.tooltip : undefined}
            >
              <Icon 
                name={item?.icon} 
                size={20} 
                color={isActive ? 'currentColor' : 'currentColor'} 
              />
              {!isCollapsed && <span>{item?.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* User Section */}
      <div className="px-4 py-4 border-t border-border">
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
          <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="User" size={16} color="var(--color-muted-foreground)" />
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {userProfile?.full_name || 'Loading...'}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {userRole === 'admin' ? 'Administrator' : 
                 userRole === 'hr_manager' ? 'HR Manager' : 
                 userRole === 'supervisor' ? 'Supervisor' : 'Employee'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Desktop Sidebar */}
      <aside className={`
        hidden md:flex fixed left-0 top-0 h-full bg-card border-r border-border sidebar-shadow z-30
        transition-smooth
        ${isCollapsed ? 'w-16' : 'w-240'}
      `}>
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar */}
      <aside className={`
        fixed left-0 top-0 h-full w-64 sm:w-80 bg-card border-r border-border modal-shadow z-50 md:hidden
        transform transition-transform duration-300 ease-in-out
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {sidebarContent}
      </aside>
    </>
  );
};

export default Sidebar;